import { useCallback } from 'react';

import {
   HealthcareFacilityLocation,
   HealthcareFacilityLocationSpecialty,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController, useTranslate } from '@dreryk/react-common';
import { AutoLayout } from '@dreryk/react-components';

import { StyledFormSectionHeader } from '../../../../../common/StyledFormSectionHeader';
import { healthcareOrganizationTranslationKeys } from '../../../../../translations';
import {
   createHealthcareAddressFormBean,
   HealthcareAddressFormBean,
   HealthcareAddressFormPart,
} from '../../../../common/HealthcareAddressFormPart';
import { HealthcareBasicData } from '../../../../common/HealthcareBasicData';
import {
   createHealthcareContactFormBean,
   HealthcareContactFormBean,
   HealthcareContactFormPart,
} from '../../../../common/HealthcareContactFormPart';
import { HealthcareIdFormController } from '../../../../common/HealthcareIdFormController';
import { HealthcareProviderNameInput } from '../../../../common/HealthcareProviderNameInput';
import { ScopeOfProvidedServicesInteractiveTable } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable';
import { ScopeOfProvidedServicesFormBeanPart } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.types';
import { mapScopeOfProvidedServicesToFormBeanPart } from '../../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.utils';
import { LocationCodeInput } from '../LocationCodeInput';
import { SpecialtyAutosuggest } from '../SpecialtyAutosuggest';

export type HealthcareFacilityLocationUpdateFormBean = {
   id: number;
   name: string;
   locationCode: string | null;
   specialty: HealthcareFacilityLocationSpecialty;
} & HealthcareAddressFormBean &
   HealthcareContactFormBean &
   ScopeOfProvidedServicesFormBeanPart;

export type HealthcareFacilityLocationUpdateFormController = FormContextValues<HealthcareFacilityLocationUpdateFormBean>;

const getInitialValues = (
   healthcareFacilityLocation: HealthcareFacilityLocation
): HealthcareFacilityLocationUpdateFormBean => {
   return {
      id: healthcareFacilityLocation.id,
      name: healthcareFacilityLocation.name,
      locationCode: healthcareFacilityLocation.locationCode ?? null,
      specialty: healthcareFacilityLocation.specialty ?? null,
      ...mapScopeOfProvidedServicesToFormBeanPart(healthcareFacilityLocation.scopeOfProvidedServicesList),
      ...createHealthcareAddressFormBean(
         healthcareFacilityLocation.usingHealthcareFacilityUnitAddressData,
         healthcareFacilityLocation.address
      ),
      ...createHealthcareContactFormBean(
         healthcareFacilityLocation.usingHealthcareFacilityUnitContactData,
         healthcareFacilityLocation
      ),
   };
};

type HealthcareFacilityLocationUpdateFormRenderProps = Pick<
   HealthcareFacilityLocationUpdateFormController,
   'handleSubmit' | 'formState'
> & {
   setLocationCodeNotUniqueErrorMessage: VoidFunction;
};

type HealthcareFacilityLocationUpdateFormProps = {
   readonly healthcareFacilityLocation: HealthcareFacilityLocation;
   readonly healthcareParentData: HealthcareBasicData;
   readonly children: (renderProps: HealthcareFacilityLocationUpdateFormRenderProps) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const HealthcareFacilityLocationUpdateForm = (props: HealthcareFacilityLocationUpdateFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState,
      watch,
      setError,
   } = useFormController<HealthcareFacilityLocationUpdateFormBean>(getInitialValues(props.healthcareFacilityLocation));
   const translate = useTranslate();

   const { usingParentAddressData, usingParentContactData } = watch([
      'usingParentAddressData',
      'usingParentContactData',
   ]);

   const setLocationCodeNotUniqueErrorMessage = useCallback(() => {
      setError('locationCode', { message: healthcareOrganizationTranslationKeys.codeMustBeUniqueWithinOrganization });
   }, [setError]);

   return (
      <AutoLayout as={'div'} $direction={'vertical'} $spacing={12} $horizontalResizing={'fill'}>
         <StyledFormSectionHeader>
            {translate(healthcareOrganizationTranslationKeys.healthcareFacilityLocationData)}
         </StyledFormSectionHeader>

         <AutoLayout as={'form'} noValidate $direction={'vertical'} $horizontalResizing={'fill'} $spacing={36}>
            <HealthcareIdFormController name={'id'} register={register} required />
            <AutoLayout
               as={HealthcareProviderNameInput}
               name={'name'}
               register={register}
               required
               $horizontalResizing={'fill'}
            />

            <HealthcareAddressFormPart
               register={register}
               usingParentAddressData={usingParentAddressData}
               usingParentAddressDataLabel={
                  healthcareOrganizationTranslationKeys.usingHealthcareFacilityUnitAddressData
               }
               healthcareParentData={props.healthcareParentData}
            />

            <HealthcareContactFormPart
               register={register}
               usingParentContactData={usingParentContactData}
               usingParentContactDataLabel={
                  healthcareOrganizationTranslationKeys.usingHealthcareFacilityUnitContactData
               }
               healthcareParentData={props.healthcareParentData}
            />

            <AutoLayout as={'div'} $direction={'horizontal'} $spacing={36}>
               <AutoLayout
                  as={LocationCodeInput}
                  name={'locationCode'}
                  register={register}
                  required
                  $horizontalResizing={356}
               />
               <AutoLayout
                  as={SpecialtyAutosuggest}
                  name={'specialty'}
                  register={register}
                  required
                  $horizontalResizing={357}
               />
            </AutoLayout>

            <AutoLayout as={ScopeOfProvidedServicesInteractiveTable} register={register} $horizontalResizing={'fill'} />

            {props.children({
               handleSubmit: handleSubmit,
               formState: formState,
               setLocationCodeNotUniqueErrorMessage: setLocationCodeNotUniqueErrorMessage,
            })}
         </AutoLayout>
      </AutoLayout>
   );
};
